import { Button, FoodsIconButton, Typography } from "@foodpilot/foods";
import { CircularProgress, Dialog, DialogActions, DialogContent, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

export type StrategyDeleteTarget = "pillar" | "lever";

type StrategyDeleteConfirmDialogProps = {
    open: boolean;
    target: StrategyDeleteTarget;
    name: string;
    isDeleting: boolean;
    onConfirm: () => void;
    onClose: () => void;
};

export const StrategyDeleteConfirmDialog = (props: StrategyDeleteConfirmDialogProps) => {
    const { open, target, name, isDeleting, onConfirm, onClose } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const isPillar = target === "pillar";

    const closeDialog = () => {
        if (!isDeleting) onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={closeDialog}
            maxWidth="sm"
            fullWidth
            aria-labelledby="strategy-delete-dialog-title"
            aria-describedby="strategy-delete-dialog-description"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: theme.spacing(2),
                        overflow: "hidden",
                    },
                },
            }}
        >
            <DialogContent sx={{ position: "relative", px: { xs: 3, sm: 6 }, pt: { xs: 7, sm: 6 }, pb: 5 }}>
                <FoodsIconButton
                    size="m"
                    icon="close"
                    buttonProps={{
                        "aria-label": t("settings.csr.delete_dialog.close", "Close confirmation dialog"),
                        disabled: isDeleting,
                        onClick: closeDialog,
                        sx: { position: "absolute", top: theme.spacing(2.5), right: theme.spacing(2.5) },
                    }}
                />
                <Typography
                    id="strategy-delete-dialog-title"
                    component="h2"
                    variant="huge-bold"
                    sx={{ pr: { sm: 3 }, overflowWrap: "anywhere" }}
                >
                    {isPillar ?
                        t(
                            "settings.csr.delete_dialog.pillar.title",
                            "Are you sure you want to delete the pillar ‘{{name}}’?",
                            {
                                name,
                            },
                        )
                    :   t(
                            "settings.csr.delete_dialog.lever.title",
                            "Are you sure you want to delete the lever ‘{{name}}’?",
                            {
                                name,
                            },
                        )
                    }
                </Typography>
                <Typography
                    id="strategy-delete-dialog-description"
                    variant="big"
                    color={theme.custom.grey[2100]}
                    sx={{ mt: 3 }}
                >
                    {isPillar ?
                        t(
                            "settings.csr.delete_dialog.pillar.description",
                            "If you delete it, this pillar along with all these levers will disappear from your strategy. They will no longer be visible to users and cannot be retrieved.",
                        )
                    :   t(
                            "settings.csr.delete_dialog.lever.description",
                            "If you delete it, this lever will disappear from your strategy and will no longer be visible to users. You will not be able to recover it.",
                        )
                    }
                </Typography>
            </DialogContent>
            <DialogActions
                sx={{
                    borderTop: `1px solid ${theme.custom.grey[500]}`,
                    px: { xs: 3, sm: 6 },
                    py: 3,
                    gap: 2,
                    flexDirection: "column",
                    "& > :not(style) ~ :not(style)": { ml: 0 },
                }}
            >
                <Button fullWidth variant="primary" disabled={isDeleting} onClick={onConfirm} sx={{ minHeight: 60 }}>
                    {isDeleting ?
                        <CircularProgress size={24} color="inherit" />
                    : isPillar ?
                        t("settings.csr.delete_dialog.pillar.confirm", "Yes, delete this pillar")
                    :   t("settings.csr.delete_dialog.lever.confirm", "Yes, delete this lever")}
                </Button>
                <Button fullWidth variant="ternary" disabled={isDeleting} onClick={closeDialog} sx={{ minHeight: 58 }}>
                    {t("glossary.cancel_delete", "No, cancel")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
